import type { Locale } from '@/lib/serviceCatalog';
import { FIGTURES_LINKEDIN_COMPANY_URL } from '@/lib/entityProfiles';
import { SEO_BRAND_LOGO } from '@/lib/seo';

export type AuthorProfile = {
  slug: string;
  name: string;
  jobTitle: { tr: string; en: string };
  bio: { tr: string; en: string };
  expertise: { tr: string[]; en: string[] };
  sameAs: string[];
  image?: string;
};

const AUTHORS: AuthorProfile[] = [
  {
    slug: 'figtures-engineering',
    name: 'Figtures Engineering Team',
    jobTitle: {
      tr: 'AI & Yazılım Ürün Mühendisliği',
      en: 'AI & Software Product Engineering',
    },
    bio: {
      tr: 'Figtures product studio ekibi; LLM entegrasyonu, SaaS/MVP, mobil uygulama ve production-grade yazılım teslimatı üzerine çalışır. Discovery, mimari, geliştirme ve go-live süreçlerini uçtan uca yönetir.',
      en: 'The Figtures product studio engineering team ships LLM integrations, SaaS/MVP builds, mobile apps, and production-grade software, from discovery and architecture through go-live.',
    },
    expertise: {
      tr: [
        'AI/LLM entegrasyonu ve agent mimarisi',
        'Custom software & SaaS geliştirme',
        'Mobil uygulama (iOS/Android) mühendisliği',
        'Production observability ve güvenlik',
      ],
      en: [
        'AI/LLM integration and agent architecture',
        'Custom software & SaaS development',
        'Mobile app (iOS/Android) engineering',
        'Production observability and security',
      ],
    },
    sameAs: [FIGTURES_LINKEDIN_COMPANY_URL, 'https://figtures.com/about'],
    image: SEO_BRAND_LOGO,
  },
  {
    slug: 'figtures-product',
    name: 'Figtures Product Studio',
    jobTitle: {
      tr: 'Product Studio & Ürün Stratejisi',
      en: 'Product Studio & Product Strategy',
    },
    bio: {
      tr: 'Figtures product studio; MVP kapsamı, ürün keşfi, UX ve ticari roadmap konularında startup ve kurumsal ekiplere danışmanlık sunar. Teknik uygulanabilirlik ile iş hedeflerini aynı çerçevede değerlendirir.',
      en: 'Figtures product studio advises startup and enterprise teams on MVP scoping, product discovery, UX, and commercial roadmaps, aligning technical feasibility with business outcomes.',
    },
    expertise: {
      tr: [
        'MVP kapsamlandırma ve ürün keşfi',
        'UX/UI ve AI ürün deneyimi',
        'SaaS fiyatlandırma ve büyüme',
        'Teknik due diligence',
      ],
      en: [
        'MVP scoping and product discovery',
        'UX/UI and AI product experience',
        'SaaS pricing and growth',
        'Technical due diligence',
      ],
    },
    sameAs: [FIGTURES_LINKEDIN_COMPANY_URL, 'https://figtures.com/about'],
    image: SEO_BRAND_LOGO,
  },
];

const AUTHOR_ALIASES: Record<string, string> = {
  figtures: 'figtures-engineering',
  'figtures team': 'figtures-engineering',
  'figtures engineering': 'figtures-engineering',
  'figtures engineering team': 'figtures-engineering',
  'figtures product studio': 'figtures-product',
};

export function listAuthors(): AuthorProfile[] {
  return [...AUTHORS];
}

export function getAuthorBySlug(slug: string): AuthorProfile | undefined {
  return AUTHORS.find((author) => author.slug === slug);
}

export function resolveAuthorProfile(authorName?: string | null): AuthorProfile {
  const normalized = (authorName || '').trim().toLowerCase();
  const slug = AUTHOR_ALIASES[normalized];
  if (slug) {
    return getAuthorBySlug(slug) ?? AUTHORS[0];
  }

  const byName = AUTHORS.find((author) => author.name.toLowerCase() === normalized);
  return byName ?? AUTHORS[0];
}

export function localizeAuthorText<T extends { tr: string; en: string }>(item: T, lang: Locale) {
  return item[lang];
}

export function getAuthorProfilePath(slug: string): string {
  return `/authors/${slug}`;
}

export function isBrandAuthorSlug(slug: string): boolean {
  return slug.startsWith('figtures-');
}
